import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  useProperty,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';
import { hasEngrainSitemap } from '../utils/interactiveMap';

// ============================================================================
// Component
// ============================================================================

/**
 * Interactive Map section. When the property has an Engrain Sightmap ID
 * (or embed URL), the Engrain block is used. Otherwise the ApartmentSync
 * interactive leasing map is used.
 */
export function InteractiveMapSection() {
  const property = useProperty() as Record<string, unknown>;
  const preferEngrain = hasEngrainSitemap(property);

  const engrainSlot = useBlockSlot('interactivemapsection.engrain_block', {
    defaultValue: 'engrain-sitemap',
  });
  const mapSlot = useBlockSlot('interactivemapsection.map_block', {
    defaultValue: 'apartment-sync-map',
  });

  return (
    <div className="min-h-[400px] md:min-h-[500px] flex flex-col bg-[var(--vibe-color-background)]">
      <EditableToken
        path="interactivemapsection.engrain_block"
        as="div"
        className={preferEngrain ? 'min-h-[400px] md:min-h-[500px] flex flex-col' : 'hidden'}
      >
        {preferEngrain ? (
          engrainSlot ? (
            <SectionContextProvider sectionId={engrainSlot.sectionId}>
              <engrainSlot.BlockComponent sectionId={engrainSlot.sectionId} />
            </SectionContextProvider>
          ) : (
            <div>No block selected</div>
          )
        ) : null}
      </EditableToken>
      <EditableToken
        path="interactivemapsection.map_block"
        as="div"
        className={preferEngrain ? 'hidden' : 'min-h-[400px] md:min-h-[500px] flex flex-col'}
      >
        {!preferEngrain ? (
          mapSlot ? (
            <SectionContextProvider sectionId={mapSlot.sectionId}>
              <mapSlot.BlockComponent sectionId={mapSlot.sectionId} />
            </SectionContextProvider>
          ) : (
            <div>No block selected</div>
          )
        ) : null}
      </EditableToken>
    </div>
  );
}

// ============================================================================
// Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'InteractiveMapSection',
  displayName: 'Interactive Map Section',
  tokens: {
    'interactivemapsection.engrain_block': {
      label: 'Engrain Sightmap Block',
      type: 'block',
      defaultValue: 'engrain-sitemap',
      acceptedBlockCategories: ['Embeds'],
      description:
        'Used when the property has an Engrain Sightmap ID or embed URL.',
    },
    'interactivemapsection.map_block': {
      label: 'Apartment Sync Map Block',
      type: 'block',
      defaultValue: 'apartment-sync-map',
      acceptedBlockCategories: ['Integrations'],
      description:
        'Used when no Engrain Sightmap ID is present. Falls back to the ApartmentSync interactive leasing map.',
    },
  },
  slotGroups: {},
  collectionBindings: {},
};

(InteractiveMapSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default InteractiveMapSection;
